import { Link } from "react-router-dom";
import { MapPin, Phone, Star, ArrowRight, ExternalLink, Navigation } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { ConsultationCTA } from "@/components/ConsultationCTA";
import { COMPANY } from "@/lib/constants";
import { AREAS } from "@/data/areas";

const PROFILES = [
  { name: "Google Business Profile", url: COMPANY.gbp, desc: "See our profile, hours, and reviews on Google." },
  { name: "Yelp", url: "https://www.yelp.com/biz/esprit-home-care-annandale-2", desc: "Read reviews and connect with us on Yelp." },
  { name: "Care.com", url: "https://www.care.com/b/l/esprit-homecare-llc/falls-church-va", desc: "Our verified home care listing on Care.com." },
  { name: "LinkedIn", url: "https://www.linkedin.com/company/esprit-home-healthcare-llc/", desc: "Follow our company on LinkedIn." },
  { name: "Apple Maps", url: "https://maps.apple.com/?q=Esprit%20Home%20Healthcare%20Annandale%20VA", desc: "Find and navigate to us via Apple Maps." },
  { name: "Bing Places", url: "https://www.bing.com/maps?q=Esprit+Home+Healthcare+Annandale+VA", desc: "Locate us on Bing Maps." },
];

const TESTIMONIALS = [
  { quote: "With Esprit, I don't have to leave the comfort of my home for the best personal care experience.", name: "Scott C." },
  { quote: "From the time I got discharged from the hospital, till I got home, it was a pleasant and smooth experience with Esprit.", name: "Wanda S." },
  { quote: "Esprit's clinical staff were not only professional, but compassionate too. They made me feel safe and secure.", name: "Susan B." },
];

export default function FindUs() {
  useSEO({
    title: "Where to Find Us Online | Esprit Home Healthcare — Reviews & Locations",
    description: "Find Esprit Home Healthcare on Google, Yelp, Care.com and more. Read reviews, get directions, and see the Northern Virginia, Maryland & DC areas we serve.",
    canonical: "/find-us",
  });

  return (
    <div>
      <PageHeader
        overline="Locations & Reviews"
        title="Where to Find Us Online"
        subtitle="Connect with Esprit Home Healthcare across the web, read what families say, and see the communities we proudly serve throughout the DMV."
      />

      <section className="container-page py-16 lg:py-20 grid lg:grid-cols-2 gap-12 items-start">
        {/* Map + directions */}
        <Reveal>
          <div className="rounded-[2rem] overflow-hidden border border-border shadow-lg">
            <iframe title="Esprit Home Healthcare location" width="100%" height="360" style={{ border: 0 }} loading="lazy"
              src="https://www.google.com/maps?q=4209+Evergreen+Lane+Annandale+VA+22003&z=15&output=embed" />
          </div>
          <div className="bg-[hsl(var(--muted))] rounded-2xl p-6 mt-5">
            <p className="font-heading font-bold text-xl text-primary">Esprit Home Healthcare LLC</p>
            <p className="text-muted-foreground mt-1">{COMPANY.address1}<br />{COMPANY.address2}</p>
            <p className="text-muted-foreground mt-2">Phone: <a href={COMPANY.phoneHref} className="text-primary font-semibold">{COMPANY.phone}</a></p>
            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              <a href={COMPANY.directions} target="_blank" rel="noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 px-5 font-semibold transition-colors">
                <Navigation className="w-5 h-5" /> Get Directions
              </a>
              <a href={COMPANY.gbp} target="_blank" rel="noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground h-12 px-5 font-semibold transition-colors">
                <Star className="w-5 h-5" /> Review Us on Google
              </a>
            </div>
          </div>
        </Reveal>

        {/* Profiles */}
        <Reveal delay={0.1}>
          <h2 className="font-heading font-bold text-3xl text-primary">Find Us On</h2>
          <p className="text-muted-foreground mt-2">We keep our details consistent across trusted platforms so you always reach the real Esprit team.</p>
          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            {PROFILES.map((p) => (
              <a key={p.name} href={p.url} target="_blank" rel="noreferrer" data-testid={`profile-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group flex items-start gap-3 bg-card rounded-2xl border border-border p-5 hover:border-primary transition-colors">
                <ExternalLink className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <span>
                  <span className="font-semibold text-foreground block group-hover:text-primary">{p.name}</span>
                  <span className="text-sm text-muted-foreground">{p.desc}</span>
                </span>
              </a>
            ))}
          </div>
        </Reveal>
      </section>

      {/* Testimonials */}
      <section className="bg-[hsl(var(--muted))]">
        <div className="container-page py-16">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-secondary">Reviews</p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-primary mt-2">What Families Say</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.1}>
                <figure className="h-full bg-card rounded-2xl border border-border p-7">
                  <div className="flex gap-1 text-accent mb-3">
                    {[...Array(5)].map((_, k) => <Star key={k} className="w-5 h-5 fill-accent" />)}
                  </div>
                  <blockquote className="text-lg text-foreground leading-snug">"{t.quote}"</blockquote>
                  <figcaption className="mt-4 font-semibold text-primary">— {t.name}</figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Service areas */}
      <section className="container-page py-16">
        <Reveal className="text-center max-w-2xl mx-auto">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-primary">Serving the DMV</h2>
          <p className="text-lg text-muted-foreground mt-3">Home health care across Northern Virginia, with service throughout the greater Washington, DC and Maryland region.</p>
        </Reveal>
        <div className="flex flex-wrap justify-center gap-2 mt-8 max-w-4xl mx-auto">
          {AREAS.map((a) => (
            <Link key={a.slug} to={`/service-areas/${a.slug}`} className="rounded-full bg-card border border-border hover:bg-primary hover:text-primary-foreground px-4 py-2 text-sm font-medium transition-colors">
              {a.name}, VA
            </Link>
          ))}
        </div>
        <div className="text-center mt-8">
          <Link to="/service-areas" className="inline-flex items-center gap-2 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 px-6 font-semibold transition-colors">
            View All Service Areas <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <ConsultationCTA />
    </div>
  );
}
